import { useCallback, useMemo } from 'react';
import { getRouteApi } from '@tanstack/react-router';
import type {
  PcmpMisStatusReportParams,
  PcmpMisStatusReportSearch,
} from '../types/pcmpMisStatusReport.types';
import { usePcmpMisStatusSubStatus } from './usePcmpMisStatusSubStatus';
import { usePcmpReportFilters } from './usePcmpReportFilters';

const route = getRouteApi('/_auth/pcmp/reports/mis-status/');

// Reset clears the member and the reason with the shared filters.
const CLEARED_EXTRA: Partial<PcmpMisStatusReportSearch> = {
  misUserId: undefined,
  subStatus: undefined,
};

/** The MIS Status report's filters, plus its member and reason, which apply at once. */
export function usePcmpMisStatusReportFilters() {
  const search = route.useSearch();
  const navigate = route.useNavigate();

  const setSearch = useCallback(
    (update: (prev: PcmpMisStatusReportSearch) => PcmpMisStatusReportSearch) => {
      void navigate({ search: update });
    },
    [navigate],
  );

  const { misUserId } = search;
  const { subStatus, isReady } = usePcmpMisStatusSubStatus(search.subStatus);

  const report = usePcmpReportFilters(search, setSearch, {
    activeCount: (misUserId === undefined ? 0 : 1) + (subStatus === undefined ? 0 : 1),
    cleared: CLEARED_EXTRA,
  });
  const { appliedFilters } = report;

  const params = useMemo<PcmpMisStatusReportParams>(
    () => ({ ...appliedFilters, misUserId, subStatus }),
    [appliedFilters, misUserId, subStatus],
  );

  const setMisUserId = useCallback(
    (next: number | undefined) => {
      setSearch((prev) => ({ ...prev, misUserId: next }));
    },
    [setSearch],
  );

  const setSubStatus = useCallback(
    (next: string | undefined) => {
      setSearch((prev) => ({ ...prev, subStatus: next }));
    },
    [setSearch],
  );

  return { ...report, misUserId, subStatus, isReady, params, setMisUserId, setSubStatus };
}
